import { CreateBomSupplierInfoModal } from "@/components/bom/create-bom-supplier-info-modal";
import { EditBomSupplierInfoModal } from "@/components/bom/edit-bom-supplier-info-modal";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import Pagination from "@/components/core/pagination";
import { Content } from "@/components/layouts/content";
import { bomSupplierInfosQueryOptions, useBomSupplierInfoDeleteMutation } from "@/features/dashboard/api/queries/bom-supplier-info-query";
import type { BomSupplierInfo } from "@/features/dashboard/types/bom-supplier-info";
import { queryClient } from "@/query-client";
import { IconPlus, IconSearch } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";

const columns: ColumnDef<BomSupplierInfo>[] = [
	{
		id: "supplier_name",
		label: "Supplier",
		cellClassName: "min-w-[200px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<Link to="/bom/supplier-infos/$supplierInfoId" params={{ supplierInfoId: data.id! }}>
				<span className="font-semibold text-blue-600 hover:underline">{data.supplier_name ?? "-"}</span>
			</Link>
		),
	},
	{
		id: "contact_person",
		label: "Contact Person",
		cellClassName: "min-w-[180px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<div className="flex flex-col space-y-1">
				<span className="font-medium">{data.contact_person ?? "-"}</span>
				{data.position && <span className="text-sm text-gray-500">{data.position}</span>}
			</div>
		),
	},
	{
		id: "email",
		label: "Email",
		cellClassName: "min-w-[180px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-700">{data.email ?? "-"}</span>,
	},
	{
		id: "mobile_number",
		label: "Mobile",
		cellClassName: "min-w-[130px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => (
			<div className="flex flex-col space-y-1">
				{data.mobile_number && <span>{data.mobile_number}</span>}
				{data.landline_number && <span className="text-sm text-gray-500">{data.landline_number}</span>}
				{!data.mobile_number && !data.landline_number && <span className="text-gray-400">-</span>}
			</div>
		),
	},
	{
		id: "address",
		label: "Address",
		cellClassName: "min-w-[200px] text-left",
		headerClassName: "whitespace-nowrap",
		cell: ({ data }) => <span className="text-gray-700">{data.address ?? "-"}</span>,
	},
];

export const Route = createFileRoute("/_dashboard/bom_/supplier-infos")({
	validateSearch: z.object({
		"filter[search]": z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().optional(),
	}),
	component: Page,
});

function Page() {
	const navigate = useNavigate();
	const search = Route.useSearch();
	const [editingSupplier, setEditingSupplier] = useState<BomSupplierInfo | null>(null);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState(search["filter[search]"] || "");
	const deleteMutation = useBomSupplierInfoDeleteMutation();

	const suppliersQuery = useQuery(bomSupplierInfosQueryOptions({ ...search }));

	const handleEdit = (supplier: BomSupplierInfo) => {
		setEditingSupplier(supplier);
		setIsEditModalOpen(true);
	};

	const handleCloseEditModal = () => {
		setIsEditModalOpen(false);
		setEditingSupplier(null);
	};

	const handleDelete = async (supplier: BomSupplierInfo) => {
		if (supplier.id) {
			await deleteMutation.mutateAsync(supplier.id);
			await queryClient.invalidateQueries({ queryKey: ["bom-supplier-infos"] });
		}
	};

	const handleSearch = async () => {
		await navigate({
			to: "/bom/supplier-infos",
			search: (old) => ({
				...old,
				"filter[search]": searchTerm || undefined,
				page: 1,
			}),
		});
	};

	const handleSearchKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter") handleSearch();
	};

	const clearSearch = async () => {
		setSearchTerm("");
		await navigate({
			to: "/bom/supplier-infos",
			search: (old) => ({
				...old,
				"filter[search]": undefined,
				page: 1,
			}),
		});
	};

	const suppliers = suppliersQuery.data?.data ?? [];
	const meta = suppliersQuery.data?.meta ?? {
		current_page: 1,
		last_page: 1,
		from: 1,
		to: 0,
		total: 0,
		per_page: 10,
	};

	if (suppliersQuery.isLoading) {
		return (
			<Content pageName="Supplier Info">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading suppliers...</p>
					</div>
				</div>
			</Content>
		);
	}

	return (
		<Content pageName="Supplier Info">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="inline-flex flex-col w-auto">
						<span className="text-xl text-foreground-dark font-semibold">Supplier Information</span>
						<span className="text-base text-foreground/70">Manage your supplier information</span>
					</div>
					<Button onClick={() => setIsCreateModalOpen(true)}>
						<IconPlus size={18} stroke={2} />
						<span>Add Supplier</span>
					</Button>
				</div>
				<div className="flex flex-col w-full gap-4">
					<div className="flex items-center gap-2 w-full max-w-md">
						<div className="relative flex-1">
							<IconSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
							<input
								type="text"
								placeholder="Search supplier name, email, address..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								onKeyDown={handleSearchKeyDown}
								className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
							/>
							{searchTerm && (
								<button type="button" onClick={clearSearch} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
									×
								</button>
							)}
						</div>
						<Button onClick={handleSearch} variant="outline">
							Search
						</Button>
					</div>
					<DataTable
						className="border-collapse"
						data={suppliers}
						columns={columns}
						stickyHead={false}
						stickyCol={false}
						showBorder={true}
						compact={true}
						showToolbar={false}
						onEdit={handleEdit}
						onDelete={handleDelete}
					/>
					<Pagination
						onClick={async (page) => {
							await navigate({
								to: "/bom/supplier-infos",
								search: (old) => ({ ...old, page }),
							});
						}}
						initialPage={meta.current_page}
						pageCount={meta.last_page}
						totalItemCount={meta.total}
					/>
				</div>
			</div>
			<CreateBomSupplierInfoModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onSuccess={() => suppliersQuery.refetch()} />
			{editingSupplier && (
				<EditBomSupplierInfoModal
					supplierInfo={editingSupplier}
					isOpen={isEditModalOpen}
					onClose={handleCloseEditModal}
					onSuccess={() => suppliersQuery.refetch()}
				/>
			)}
		</Content>
	);
}
